import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty, isEqual, pick } from 'lodash-es';

import { usePrevious } from 'ha/helpers/hooks/usePrevious';

import { RequiredAnalyticsFilters } from '../constants';
import areCoordsEqual from '../utils/areCoordsEqual';
import {
  getAnalyticsForFilters,
  getSearchParams,
  getMapParams,
  getCityCanonical,
} from '../selectors';
import { trackFilterApplied } from '../actions';

const pickRequiredAnalytics = (value?: {}) =>
  pick(value, Object.keys(RequiredAnalyticsFilters));

const useFiltersAppliedAnalytics = () => {
  const dispatch = useDispatch();
  const filtersAnalytics = useSelector(getAnalyticsForFilters);
  const searchParams = useSelector(getSearchParams);
  const mapParams = useSelector(getMapParams);
  const cityCanonical = useSelector(getCityCanonical);
  const prevSearchParams = usePrevious(searchParams);
  const prevMapParams = usePrevious(mapParams);
  const prevCityCanonical = usePrevious(cityCanonical);

  const mapParamsAreEqual = useCallback(
    (mapState, prevMapState) =>
      mapState.zoomLevel === prevMapState.zoomLevel &&
      areCoordsEqual(prevMapState.latitude, mapState.latitude) &&
      areCoordsEqual(prevMapState.longitude, mapState.longitude),
    [],
  );

  useEffect(() => {
    const prevFilters = pickRequiredAnalytics(prevSearchParams);
    const filters = pickRequiredAnalytics(searchParams);

    const cityChanged =
      prevCityCanonical && prevCityCanonical !== cityCanonical;

    if (
      (!isEmpty(prevFilters) && !isEqual(prevFilters, filters)) ||
      (!isEmpty(prevMapParams) &&
        !mapParamsAreEqual(prevMapParams, mapParams)) ||
      cityChanged
    ) {
      const changedFilters = Object.keys(filters).filter(
        key => !isEqual(prevFilters[key], filters[key]),
      );
      const changedMapParams = Object.keys(mapParams).filter(
        key => prevMapParams?.[key] !== mapParams[key],
      );

      // eslint-disable-next-line no-restricted-syntax
      for (const key of Object.keys(filtersAnalytics)) {
        const el = filtersAnalytics[key];

        if (Array.isArray(el) || el === '') {
          if (el.length === 0) {
            delete filtersAnalytics[key];
          }
        }

        if (el === null) {
          delete filtersAnalytics[key];
        }
      }

      dispatch(
        trackFilterApplied({
          ...filtersAnalytics,
          searchChanges: [
            ...changedFilters,
            ...changedMapParams,
            ...(cityChanged ? ['cityCanonical'] : []),
          ],
        }),
      );
    }
  }, [
    filtersAnalytics,
    searchParams,
    prevSearchParams,
    mapParams,
    prevMapParams,
    mapParamsAreEqual,
    cityCanonical,
    prevCityCanonical,
    dispatch,
  ]);
};

export default useFiltersAppliedAnalytics;
